import React, { useEffect, useState } from "react";
import { Form, Input, Button, Typography, Space, message, Progress, Modal } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;

interface LoginFormValues {
  username: string;
  password: string;
}

const LoginForm: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      setTimeout(() => navigate('/dashboard'), 300);
    }
  }, [isAuthenticated, navigate]);
  const { login } = useAuth();
  const onFinish = async (values: LoginFormValues) => {
    await handleLoginSubmit(values, false);
  };

  const handleLoginSubmit = async (values: LoginFormValues, override: boolean = false) => {
    try {
      setIsLoading(true);

      if (!process.env.REACT_APP_BACKEND_URL) {
        throw new Error('REACT_APP_BACKEND_URL is not defined');
      }

      const trimmedValues = {
        username: values.username.trim(),
        password: values.password.trim(),
        override: override
      };

      const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(trimmedValues),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON, got: ${text.slice(0, 100)}...`);
      }

      const data = await response.json();

      if (response.ok) {

        setProgress(100);
        setIsExiting(true);
        await login(data.token);
      } else {

        message.error(data.error || 'Login failed');
      }

    } catch (error) {
      console.error('Login error:', error);


      if (error instanceof TypeError) {
        message.error('Cannot connect to server');
      }

      else if (error instanceof Error) {
        message.error(error.message);
      }

      else {
        message.error('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any): void => {
    console.error("Failed:", errorInfo);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "linear-gradient(135deg, #1890ff, #91d5ff)",
        opacity: isExiting ? 0 : 1,
        transition: "opacity 0.2s ease-in-out",
      }}
    >
      <div
        style={{
          padding: "40px",
          background: "#fff",
          borderRadius: "10px",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          width: "400px",
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          { }
          <div style={{ textAlign: "center" }}>
            <img
              src={require("../icon/amp_login.webp")}
              alt="Logo"
              style={{ width: "200px", marginBottom: "20px" }}
            />
          </div>

          { }
          <Title level={3} style={{ textAlign: "center", marginBottom: "20px" }}>
            Login Users
          </Title>
          {showProgress ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <Title level={4} style={{ marginBottom: "15px" }}>Logging in...</Title>
              <Progress percent={progress} status="active" strokeColor={{ '0%': '#1890ff', '100%': '#52c41a' }} />
            </div>
          ) : (

            <Form
              name="login"
              initialValues={{ remember: true }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              { }
              <Form.Item
                name="username"
                rules={[{ required: true, message: "Please enter your username!" }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Username"
                  size="large"
                />
              </Form.Item>

              { }
              <Form.Item
                name="password"
                rules={[{ required: true, message: "Please enter your password!" }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  size="large"
                />
              </Form.Item>

              { }
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={isLoading}
                  style={{
                    background: "linear-gradient(135deg, #1890ff, #40a9ff)",
                    border: "none",
                    transition: "all 1s ease"
                  }}
                >
                  {isLoading ? 'Memproses...' : 'Login'}
                </Button>
              </Form.Item>
            </Form>
          )}
        </Space>
      </div>
    </div>
  );
};

export default LoginForm;
